package com.zi.support.web

import com.zi.support.shared.AssetRegistry
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.events.Event

/**
 * More > Asset registry: the shared page (AssetRegistry.PAGE) in an iframe; this side answers from Firestore and, in
 * Chrome on Android, Web NFC (which only runs in the top window, not the iframe). Desktop browsers manage the list only.
 */
internal object AssetRegistryScreen {
    /** The running scan or write: cancelled by the page's X, or replaced by the next one. */
    private var nfcAbort: dynamic = null
    private var nfcRequest = 0

    /** [openAsset]: an asset id to show straight away (from an /asset/<id> link). */
    fun render(openAsset: String = "") {
        element("content")?.innerHTML = """
          $MORE_BACK
          <div class="page-intro"><div><h2>Asset registry</h2>
            <p>Our equipment and its NFC tags. Scan a tag with the Support App to open its asset, or to register a new one.</p></div></div>
          <iframe id="assets-frame" class="locations-frame" title="Asset registry"></iframe>
        """.trimIndent()
        bindMoreBack()
        val frame: dynamic = element("assets-frame")
        frame.onload = {
            frame.contentWindow.setTheme(if (document.documentElement?.getAttribute("data-theme") == "dark") "dark" else "light")
            frame.contentWindow.setNfc(window.asDynamic().NDEFReader != undefined)
            if (openAsset.isNotEmpty()) frame.contentWindow.openAsset(openAsset)
        }
        showPage(frame, AssetRegistry.PAGE)
        lateinit var listener: (Event) -> Unit
        listener = fn@{ event ->
            val current: dynamic = element("assets-frame") ?: run { window.removeEventListener("message", listener); cancelNfc(); return@fn }
            val data: dynamic = event.asDynamic().data
            if (event.asDynamic().source != current.contentWindow || data == null || jsTypeOf(data.assets) != "string") return@fn
            answer(current, JSON.parse(data.assets as String))
        }
        window.addEventListener("message", listener)
    }

    private fun answer(frame: dynamic, request: dynamic) {
        val id = request.id as Int
        fun reply(rows: dynamic) { frame.contentWindow.assetsReply(id, rows, null) }
        fun fail(message: String) { frame.contentWindow.assetsReply(id, null, message) }
        val collection = AssetRegistry.COLLECTION
        when (request.type as String) {
            "list" -> promise(FirebaseGateway.getCollection(collection), { snapshot -> reply(rows(snapshot.docs)) }, ::fail)
            "get" -> promise(FirebaseGateway.getDocument(request.path as String), { snapshot ->
                reply(if (snapshot.exists() as Boolean) rows(arrayOf(snapshot)) else js("[]"))
            }, ::fail)
            "save" -> {
                val data: dynamic = request.data
                for (stamp in request.stamp as Array<String>) {
                    data["${stamp}At"] = FirebaseGateway.serverTimestamp()
                    data["${stamp}By"] = WebApp.currentName
                }
                promise(FirebaseGateway.set("$collection/${request.assetId}", data, merge = true), { reply(js("[]")) }, ::fail)
            }
            "delete" -> promise(FirebaseGateway.delete("$collection/${request.assetId}"), { reply(js("[]")) }, ::fail)
            "scan", "write" -> nfc(id, request, ::reply, ::fail)
            "cancel" -> { cancelNfc(); reply(js("[]")) }
        }
    }

    /** Firestore documents as the page's rows: fields plus "path", timestamps as epoch ms. */
    private fun rows(docs: Array<dynamic>): dynamic {
        val rows = js("[]")
        for (doc in docs) {
            val fields: dynamic = doc.data()
            val row: dynamic = json("path" to doc.ref.path)
            for (key in js("Object.keys")(fields) as Array<String>) {
                val value: dynamic = fields[key]
                row[key] = if (value != null && jsTypeOf(value.toMillis) == "function") value.toMillis() else value
            }
            rows.push(row)
        }
        return rows
    }

    private fun nfc(id: Int, request: dynamic, reply: (dynamic) -> Unit, fail: (String) -> Unit) {
        if (window.asDynamic().NDEFReader == undefined) { fail("This browser can't read NFC tags. Use the Support App, or Chrome on Android."); return }
        cancelNfc()
        val controller: dynamic = js("Reflect").construct(js("AbortController"), js("[]"))
        val reader: dynamic = js("Reflect").construct(js("NDEFReader"), js("[]"))
        nfcAbort = controller
        nfcRequest = id
        val options: dynamic = json("signal" to controller.signal)
        if (request.type == "write") {
            val records = (request.records as Array<String>).map { json("recordType" to (if (it.startsWith("https://")) "url" else "text"), "data" to it) }.toTypedArray()
            promise(reader.write(json("records" to records), options), {
                nfcAbort = null
                // Web NFC doesn't say which tag it wrote: "" keeps the asset's tag UID as it is.
                reply(arrayOf(json("uid" to "", "chip" to "", "capacity" to 0, "records" to request.records)))
            }, { message -> if (nfcRequest == id) { nfcAbort = null; fail(message) } })
            return
        }
        reader.onreading = { event: dynamic ->
            controller.abort()
            nfcAbort = null
            val texts = js("[]")
            for (record in event.message.records as Array<dynamic>) {
                if (record.recordType == "text" || record.recordType == "url") {
                    texts.push(js("Reflect").construct(js("TextDecoder"), arrayOf(record.encoding ?: "utf-8")).decode(record.data))
                }
            }
            reply(arrayOf(json("uid" to (event.serialNumber ?: ""), "chip" to "", "capacity" to 0, "records" to texts)))
        }
        reader.onreadingerror = { _: dynamic -> fail("Couldn't read that tag. Hold it still against the phone and try again.") }
        promise(reader.scan(options), {}, { message -> if (nfcRequest == id) { nfcAbort = null; fail(message) } })
    }

    private fun cancelNfc() {
        nfcAbort?.abort()
        nfcAbort = null
        nfcRequest = 0
    }
}
